import { Marble } from './marble';
import { StageDef } from './data/maps';
import { initialZoom, zoomThreshold } from './data/constants';
import { VectorLike } from './types/VectorLike';

export class Camera {
  private _position: VectorLike = { x: 0, y: 0 };
  private _targetPosition: VectorLike = { x: 0, y: 0 };
  private _zoom: number = 1;
  private _targetZoom: number = 1;
  private _locked = false;
  private _shouldFollowMarbles = false;
  private _alternateViewTimer: number = 0;
  private _showingFrontMarble: boolean = false;
  private _viewSwitchInterval: number = 5000; // 5초마다 전환

  get zoom() {
    return this._zoom;
  }
  set zoom(v: number) {
    this._targetZoom = v;
  }

  get x() {
    return this._position.x;
  }
  set x(v: number) {
    this._targetPosition.x = v;
  }
  get y() {
    return this._position.y;
  }
  set y(v: number) {
    this._targetPosition.y = v;
  }

  get position() {
    return this._position;
  }

  setPosition(v: VectorLike, force: boolean = false) {
    if (force) {
      return (this._position = { x: v.x, y: v.y });
    }
    return (this._targetPosition = { x: v.x, y: v.y });
  }

  lock(v: boolean) {
    this._locked = v;
  }

  startFollowingMarbles() {
    this._shouldFollowMarbles = true;
    this._alternateViewTimer = 0;
    this._showingFrontMarble = false;
  }

  initializePosition() {
    const centerX = 12.95;
    const initialY = 2;

    this._position = { x: centerX, y: initialY };
    this._targetPosition = { x: centerX, y: initialY };
    this._shouldFollowMarbles = false;
  }

  update({
    marbles,
    stage,
    needToZoom,
    targetIndex,
    winnerRank,
    winnersCount,
    totalMarbleCount,
  }: {
    marbles: Marble[];
    stage: StageDef;
    needToZoom: boolean;
    targetIndex: number;
    winnerRank: number;
    winnersCount: number;
    totalMarbleCount: number;
  }) {
    // set target position
    if (!this._locked) {
      this._calcTargetPositionAndZoom(marbles, stage, needToZoom, targetIndex, winnerRank, winnersCount, totalMarbleCount);
    }

    // interpolate position
    this._position.x = this._interpolation(this.x, this._targetPosition.x);
    this._position.y = this._interpolation(this.y, this._targetPosition.y);

    // interpolate zoom
    this._zoom = this._interpolation(this._zoom, this._targetZoom);
  }

  private _calcTargetPositionAndZoom(
    marbles: Marble[],
    stage: StageDef,
    needToZoom: boolean,
    targetIndex: number,
    winnerRank: number,
    winnersCount: number,
    totalMarbleCount: number
  ) {
    if (!this._shouldFollowMarbles) {
      return;
    }

    if (marbles.length === 0) {
      this.zoom = 1;
      return;
    }

    // 마지막 당첨 순위인지 확인
    const isLastWinner = winnerRank >= totalMarbleCount - 1;
    const hasWinners = winnersCount > 0;

    // 마지막 당첨 모드이고, 첫 번째 구슬이 이미 통과했으며, 구슬이 3개 이상 남았을 때
    if (isLastWinner && hasWinners && marbles.length >= 3) {
      const frontMarble = marbles[0]; // 제일 앞 구슬 (결승지점 기준)
      
      // 구슬이 10개 이하면: 카메라 전환 없이 제일 앞 구슬 고정 + 줌인 2배 + 슬로우모션
      if (marbles.length <= 10) {
        this.setPosition({ x: frontMarble.x, y: frontMarble.y - 2 });
        this.zoom = 2;
        return;
      }
      
      // 구슬이 11개 이상일 때: 기존 카메라 전환 로직
      const backMarble = marbles[marbles.length - 1]; // 제일 뒤 구슬
      const heightDifference = Math.abs(frontMarble.y - backMarble.y);

      // 높이 차이가 10 이상일 때만 카메라 전환
      if (heightDifference > 10) {
        this._alternateViewTimer += 10; // 10ms씩 증가

        if (this._alternateViewTimer >= this._viewSwitchInterval) {
          this._alternateViewTimer = 0;
          this._showingFrontMarble = !this._showingFrontMarble;
        }

        if (this._showingFrontMarble) {
          // 제일 앞 구슬을 보여줄 때: 높이를 -2 위치로
          this.setPosition({ x: frontMarble.x, y: frontMarble.y - 2 });
        } else {
          // 제일 뒤 구슬을 보여줄 때
          this.setPosition(backMarble.position);
        }

        // 줌은 기본 1배
        this.zoom = 1;
        return;
      }
    }

    // 일반 모드 또는 높이 차이가 크지 않을 때
    const targetMarble = marbles[targetIndex] ? marbles[targetIndex] : marbles[0];
    this.setPosition(targetMarble.position);

    // 첫번째 당첨 모드이고 결승지점 근처일 때 줌인 2배
    if (!isLastWinner && needToZoom) {
      const goalDist = Math.abs(stage.zoomY - this._position.y);
      if (goalDist < zoomThreshold * 4.5) {
        this.zoom = 2;
      } else {
        this.zoom = Math.max(2, (1 - goalDist / zoomThreshold) * 3);
      }
    // 마지막 당첨 모드이고 구슬이 10개 이하 남고 결승 근처일 때 줌인 3배
    } else if (isLastWinner && marbles.length <= 10 && needToZoom) {
      const goalDist = Math.abs(stage.zoomY - this._position.y);
      if (goalDist < zoomThreshold * 4.5) {
        this.zoom = 3;
      } else {
        this.zoom = Math.max(3, (1 - goalDist / zoomThreshold) * 3);
      }
    } else if (needToZoom) {
      const goalDist = Math.abs(stage.zoomY - this._position.y);
      this.zoom = Math.max(2, (1 - goalDist / zoomThreshold) * 3);
    } else {
      this.zoom = 1;
    }
  }

  private _interpolation(current: number, target: number) {
    const d = target - current;
    if (Math.abs(d) < 1 / initialZoom) {
      return target;
    }

    return current + d / 10;
  }

  renderScene(
    ctx: CanvasRenderingContext2D,
    callback: (ctx: CanvasRenderingContext2D) => void
  ) {
    const zoomFactor = initialZoom * 2 * this._zoom;
    ctx.save();
    ctx.translate(-this.x * this._zoom, -this.y * this._zoom);
    ctx.scale(this.zoom, this.zoom);
    ctx.translate(
      ctx.canvas.width / zoomFactor,
      ctx.canvas.height / zoomFactor
    );
    callback(ctx);
    ctx.restore();
  }
}